import { useCallback, useEffect, useMemo, useState } from 'react';
import { api } from './api.js';

interface Per100 {
  kcal: number; protein: number; carb: number; fat: number;
  fibre: number | null; sugar: number | null; satFat: number | null; sodiumMg: number | null;
}

interface Portion { label: string; grams: number }

interface Food {
  id: string; name: string; brand: string | null; barcode: string | null; unit: string;
  per100: Per100; source: string; confidence: string; retailers: string[];
  packSize: string | null; servingLabel: string | null; notes: string | null;
  sourceUrl: string | null; isMine: boolean; isFavourite: boolean; myLogCount: number;
  portions: Portion[];
}

interface Macros { kcal: number; proteinG: number; carbG: number; fatG: number }

interface Entry {
  id: string; kind: 'food' | 'recipe' | 'quick'; mealSlot: string; name: string;
  brand: string | null; unit: string; grams: number | null; source: string;
  foodSource: string | null; confidence: string | null;
  kcal: number; proteinG: number; carbG: number; fatG: number;
}

interface Meal {
  slot: string; entries: Entry[]; totals: Macros; share: number;
  target: Macros | null; remaining: Macros | null;
}

interface Day {
  date: string; entries: Entry[]; meals: Meal[];
  totals: Macros; target: Macros | null; remaining: Macros | null;
}

const SLOTS = ['breakfast', 'shake', 'lunch', 'snack', 'dinner'] as const;
const RETAILERS = ['Checkers', 'Pick n Pay', 'Spar', 'Woolworths'] as const;

const n1 = (v: number): string => (Number.isInteger(v) ? String(v) : v.toFixed(1));
const shift = (date: string, days: number): string => {
  const d = new Date(`${date}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
};
const today = (): string => new Date().toISOString().slice(0, 10);

/**
 * Provenance travels with every number. You should never see a macro without
 * knowing whether it came off a real label, a composition table, a shopper's
 * phone camera, or your own typing.
 */
function Provenance({ source, confidence }: { source: string; confidence: string }): JSX.Element {
  const label = source === 'verified_label' ? 'label'
    : source === 'composition_table' ? 'table'
    : source === 'off' ? 'scanned'
    : source === 'sa_database' ? 'SA database'
    : source === 'user' ? 'yours'
    : 'generic';
  return <span className={`chip chip-${confidence}`}>{label}</span>;
}

export function Log({ onLogged }: { onLogged: () => void }): JSX.Element {
  const [date, setDate] = useState(today());
  const [q, setQ] = useState('');
  const [retailer, setRetailer] = useState<string | null>(null);
  const [shortlist, setShortlist] = useState<'recent' | 'favourites'>('recent');
  const [foods, setFoods] = useState<Food[]>([]);
  const [slot, setSlot] = useState<(typeof SLOTS)[number]>('breakfast');
  const [grams, setGrams] = useState<Record<string, string>>({});
  const [day, setDay] = useState<Day | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [quick, setQuick] = useState({ label: '', kcal: '', proteinG: '', carbG: '', fatG: '' });

  const browsing = !q.trim() && !retailer;

  const refreshDay = useCallback(async (): Promise<void> => {
    const d = await api<Day>(`/log/day?date=${date}`);
    setDay(d);
    onLogged();
  }, [date, onLogged]);

  useEffect(() => { void refreshDay(); }, [refreshDay]);

  const url = useMemo(() => {
    const p = new URLSearchParams({ q, limit: '40' });
    if (retailer) p.set('retailer', retailer);
    return `/foods/search?${p.toString()}`;
  }, [q, retailer]);

  useEffect(() => {
    const h = setTimeout(() => {
      const source = browsing
        ? api<{ results: Food[] }>(shortlist === 'favourites' ? '/foods/favourites' : '/foods/recent')
        : api<{ results: Food[] }>(url);
      source.then((r) => setFoods(r.results)).catch(() => undefined);
    }, 200);
    return () => clearTimeout(h);
  }, [url, browsing, shortlist]);

  async function add(f: Food, g: number): Promise<void> {
    if (!g || g <= 0) { setMsg('Enter a weight first'); return; }
    await api('/log', {
      method: 'POST',
      body: JSON.stringify({ foodId: f.id, grams: g, mealSlot: slot, logDate: date }),
    });
    setMsg(`Logged ${g}${f.unit} ${f.name}`);
    setGrams((s) => ({ ...s, [f.id]: '' }));
    await refreshDay();
  }

  async function star(f: Food): Promise<void> {
    const next = !f.isFavourite;
    await api(`/foods/${f.id}/favourite`, { method: next ? 'PUT' : 'DELETE' });
    setFoods((s) => s.map((x) => (x.id === f.id ? { ...x, isFavourite: next } : x)));
  }

  async function addQuick(): Promise<void> {
    const num = (v: string): number | undefined => (v.trim() === '' ? undefined : Number(v));
    const kcal = num(quick.kcal);
    if (kcal === undefined || Number.isNaN(kcal)) { setMsg('A quick add needs at least an energy figure'); return; }
    const macros = [quick.proteinG, quick.carbG, quick.fatG].filter((v) => v.trim() !== '').length;
    if (macros !== 0 && macros !== 3) {
      setMsg('Give all three macros or none — two of three would quietly under-count the day');
      return;
    }
    await api('/log', {
      method: 'POST',
      body: JSON.stringify({
        mealSlot: slot, logDate: date, kcal,
        label: quick.label.trim() || undefined,
        proteinG: num(quick.proteinG), carbG: num(quick.carbG), fatG: num(quick.fatG),
      }),
    });
    setQuick({ label: '', kcal: '', proteinG: '', carbG: '', fatG: '' });
    setMsg('Quick add logged');
    await refreshDay();
  }

  async function copyFrom(days: number, only: string | null): Promise<void> {
    try {
      await api('/log/copy', {
        method: 'POST',
        body: JSON.stringify({ from: shift(date, -days), to: date, mealSlot: only ?? undefined }),
      });
      setMsg(only ? `Copied ${only}` : 'Copied the day');
      await refreshDay();
    } catch {
      setMsg('Nothing logged on that day to copy');
    }
  }

  async function removeEntry(id: string): Promise<void> {
    await api(`/log/${id}`, { method: 'DELETE' });
    await refreshDay();
  }

  return (
    <>
      <div className="card">
        <div className="row" style={{ justifyContent: 'space-between' }}>
          <h2 style={{ margin: 0 }}>Log food</h2>
          <div className="row">
            <button type="button" onClick={() => setDate(shift(date, -1))}>←</button>
            <span className="muted small">{date === today() ? 'Today' : date}</span>
            <button type="button" disabled={date >= today()} onClick={() => setDate(shift(date, 1))}>→</button>
          </div>
        </div>

        {day && (
          <p className="muted small">
            {day.totals.kcal} kcal
            {day.target ? ` of ${day.target.kcal} · ${day.remaining!.kcal} left` : ' · no target set'}
            {' · '}P{n1(day.totals.proteinG)} C{n1(day.totals.carbG)} F{n1(day.totals.fatG)}
          </p>
        )}

        <div className="row">
          <select value={slot} onChange={(e) => setSlot(e.target.value as typeof slot)}>
            {SLOTS.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <input
            placeholder="Search South African foods…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </div>

        <div className="row wrap small">
          {browsing ? (
            <>
              <button type="button" className={shortlist === 'recent' ? 'on' : ''} onClick={() => setShortlist('recent')}>Recent</button>
              <button type="button" className={shortlist === 'favourites' ? 'on' : ''} onClick={() => setShortlist('favourites')}>Favourites</button>
            </>
          ) : (
            <>
              <button type="button" className={retailer === null ? 'on' : ''} onClick={() => setRetailer(null)}>All shops</button>
              {RETAILERS.map((r) => (
                <button key={r} type="button" className={retailer === r ? 'on' : ''} onClick={() => setRetailer(retailer === r ? null : r)}>{r}</button>
              ))}
            </>
          )}
        </div>

        {msg && <p className="muted small">{msg}</p>}

        <table className="tbl">
          <thead><tr><th /><th>Food</th><th>per 100</th><th>Where</th><th>Amount</th><th /></tr></thead>
          <tbody>
            {foods.map((f) => {
              const typed = Number(grams[f.id] ?? 0);
              return (
                <tr key={f.id}>
                  <td>
                    <button type="button" title={f.isFavourite ? 'Remove star' : 'Star'} onClick={() => void star(f)}>
                      {f.isFavourite ? '★' : '☆'}
                    </button>
                  </td>
                  <td>
                    {f.brand ? <span className="muted">{f.brand} </span> : null}{f.name}{' '}
                    <Provenance source={f.source} confidence={f.confidence} />
                    {f.myLogCount > 0 && <span className="chip">×{f.myLogCount}</span>}
                    {f.notes && <div className="muted small">{f.notes}</div>}
                  </td>
                  <td className="muted small">
                    {Math.round(f.per100.kcal)} kcal<br />
                    P{n1(f.per100.protein)} C{n1(f.per100.carb)} F{n1(f.per100.fat)}
                  </td>
                  <td className="muted small">{f.retailers.length ? f.retailers.join(', ') : '—'}</td>
                  <td>
                    <input
                      className="tiny" inputMode="numeric" placeholder={f.unit}
                      value={grams[f.id] ?? ''}
                      onChange={(e) => setGrams((s) => ({ ...s, [f.id]: e.target.value }))}
                    />
                    <div className="row wrap small">
                      {f.portions.slice(0, 3).map((p) => (
                        <button key={p.label} type="button" onClick={() => setGrams((s) => ({ ...s, [f.id]: String(p.grams) }))}>
                          {p.label}
                        </button>
                      ))}
                    </div>
                  </td>
                  <td><button type="button" disabled={!(typed > 0)} onClick={() => void add(f, typed)}>Add</button></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="card">
        <h2>Quick add</h2>
        <p className="muted small">
          For a plate you cannot weigh. Energy is enough — macros only if you know them, and then all three.
        </p>
        <div className="row wrap">
          <input placeholder="What was it" value={quick.label} onChange={(e) => setQuick({ ...quick, label: e.target.value })} />
          <input className="tiny" inputMode="numeric" placeholder="kcal" value={quick.kcal} onChange={(e) => setQuick({ ...quick, kcal: e.target.value })} />
          <input className="tiny" inputMode="numeric" placeholder="P" value={quick.proteinG} onChange={(e) => setQuick({ ...quick, proteinG: e.target.value })} />
          <input className="tiny" inputMode="numeric" placeholder="C" value={quick.carbG} onChange={(e) => setQuick({ ...quick, carbG: e.target.value })} />
          <input className="tiny" inputMode="numeric" placeholder="F" value={quick.fatG} onChange={(e) => setQuick({ ...quick, fatG: e.target.value })} />
          <button type="button" onClick={() => void addQuick()}>Add to {slot}</button>
        </div>
      </div>

      <div className="card">
        <div className="row" style={{ justifyContent: 'space-between' }}>
          <h2 style={{ margin: 0 }}>The day</h2>
          <div className="row wrap small">
            <button type="button" onClick={() => void copyFrom(1, null)}>Copy yesterday</button>
            <button type="button" onClick={() => void copyFrom(1, slot)}>Copy yesterday&apos;s {slot}</button>
            <button type="button" onClick={() => void copyFrom(7, null)}>Copy last week</button>
          </div>
        </div>

        {day?.meals.map((m) => (
          <div key={m.slot} style={{ marginTop: 12 }}>
            <div className="row" style={{ justifyContent: 'space-between' }}>
              <strong style={{ textTransform: 'capitalize' }}>{m.slot}</strong>
              <span className="muted small">
                {m.totals.kcal}{m.target ? ` of ${m.target.kcal}` : ''} kcal
              </span>
            </div>
            {m.entries.length === 0 ? (
              <p className="muted small">Nothing yet.</p>
            ) : (
              <table className="rows">
                <tbody>
                  {m.entries.map((e) => (
                    <tr key={e.id}>
                      <td>
                        {e.name}
                        {e.kind === 'quick' && <span className="chip">typed</span>}
                        {e.source === 'copy' && <span className="chip">copied</span>}
                      </td>
                      <td className="dim">{e.grams != null ? `${n1(e.grams)}${e.unit}` : '—'}</td>
                      <td className="dim">{Math.round(e.kcal)} kcal</td>
                      <td className="dim">P{n1(e.proteinG)} C{n1(e.carbG)} F{n1(e.fatG)}</td>
                      <td><button type="button" onClick={() => void removeEntry(e.id)}>×</button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        ))}
      </div>
    </>
  );
}
